import React, { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import {
  AppBar,
  Box,
  CssBaseline,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Alert,
  InputAdornment
} from "@mui/material";
import {
  Menu as MenuIcon,
  Logout,
  KeyboardArrowDown,
  LockReset,
  Visibility,
  VisibilityOff,
  Brightness4,
  Brightness7,
  Refresh,
  Lightbulb
} from "@mui/icons-material";
import Sidebar from "../components/Sidebar";
import SidebarDynamic from "../components/SidebarDynamic";
import NotificationBadge from "../components/NotificationBadge";
import NotificationToast from "../components/NotificationToast";
import ReportarModal from "../components/ReportarModal";
import { useAuth } from "../contexts/AuthContext";
import { useThemeMode } from "../contexts/ThemeContext";
import { changePassword } from "../services/authService";

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [reportarModalOpen, setReportarModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { logout, user, token } = useAuth();
  const { toggleTheme, isDark } = useThemeMode();
  const theme = useTheme();

  // Determinar información del usuario según el tipo de autenticación
  const isAzureUser = user?.authProvider === 'azureAd';
  const displayName = isAzureUser
    ? user?.name
    : (user?.fullName || user?.username || "Usuario");
  const userRole = user?.roleName || (isAzureUser ? "Operador" : "Administrador");
  const userEmail = isAzureUser
    ? user?.username
    : (user?.email || "");
  const avatarLetter = isAzureUser
    ? (user?.name?.charAt(0).toUpperCase() || "U")
    : (user?.fullName?.charAt(0).toUpperCase() || user?.username?.charAt(0).toUpperCase() || "A");

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    setPasswordError("");
    setPasswordSuccess("");
    handleMenuClose();
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setPasswordData({
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
    setPasswordError("");
  };

  const handleSubmitPasswordChange = async () => {
    // Validaciones
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError("Todos los campos son requeridos");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError("Las contraseñas no coinciden");
      return;
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      setPasswordError("La nueva contraseña debe ser diferente a la actual");
      return;
    }

    setLoading(true);
    setPasswordError("");

    try {
      await changePassword(passwordData.currentPassword, passwordData.newPassword, token);
      setPasswordSuccess("Contraseña actualizada exitosamente");

      // Cerrar el modal después de 2 segundos
      setTimeout(() => {
        handleClosePasswordDialog();
      }, 2000);
    } catch (error) {
      setPasswordError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate("/login");
  };

  const handleRefresh = () => {
    // Disparar un evento personalizado que las páginas puedan escuchar
    window.dispatchEvent(new CustomEvent('refreshData'));
  };

  return (
    <Box sx={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <CssBaseline />

      {/* Modern AppBar with gradient */}
      <AppBar
        position="fixed"
        sx={{
          width: "100%",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          backdropFilter: "blur(10px)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleSidebar}
              sx={{
                mr: 2,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                }
              }}
            >
              <MenuIcon />
            </IconButton>

            {/* Logo */}
            <Box
              component="img"
              src="/logo.png"
              alt="Logo"
              sx={{
                height: 40,
                mr: 2,
                display: { xs: "none", sm: "block" }
              }}
            />

            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: "-0.02em",
                display: { xs: "none", md: "block" }
              }}
            >
              Panel de Administración
            </Typography>
          </Box>

          {/* User Menu */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Notification Badge */}
            <NotificationBadge />

            {/* Reportar Button */}
            <IconButton
              onClick={() => setReportarModalOpen(true)}
              color="inherit"
              sx={{
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                }
              }}
              aria-label="reportar idea o sugerencia"
            >
              <Lightbulb />
            </IconButton>

            {/* Refresh Button */}
            <IconButton
              onClick={handleRefresh}
              color="inherit"
              sx={{
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                }
              }}
              aria-label="refresh data"
            >
              <Refresh />
            </IconButton>

            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                flexDirection: "column",
                alignItems: "flex-end",
                mr: 1
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: alpha(theme.palette.common.white, 0.8) }}>
                {userRole}
              </Typography>
            </Box>

            <IconButton
              onClick={handleMenuOpen}
              sx={{
                p: 0.5,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.common.white, 0.1),
                }
              }}
            >
              <Avatar
                sx={{
                  width: 40,
                  height: 40,
                  bgcolor: theme.palette.secondary.main,
                  fontWeight: 600
                }}
              >
                {avatarLetter}
              </Avatar>
              <KeyboardArrowDown sx={{ ml: 0.5, color: "white" }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              PaperProps={{
                elevation: 3,
                sx: {
                  mt: 1.5,
                  minWidth: 200,
                  borderRadius: 2,
                  "& .MuiMenuItem-root": {
                    px: 2,
                    py: 1.5,
                    borderRadius: 1,
                    mx: 1,
                    my: 0.5,
                  },
                },
              }}
            >
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {displayName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {userEmail}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              {/* Dark Mode Toggle */}
              <MenuItem onClick={toggleTheme}>
                {isDark ? (
                  <>
                    <Brightness7 sx={{ mr: 1.5, fontSize: 20 }} />
                    Modo Claro
                  </>
                ) : (
                  <>
                    <Brightness4 sx={{ mr: 1.5, fontSize: 20 }} />
                    Modo Oscuro
                  </>
                )}
              </MenuItem>

              {/* Solo mostrar opción de resetear contraseña si NO es usuario de Azure AD */}
              {!isAzureUser && (
                <MenuItem onClick={handleOpenPasswordDialog}>
                  <LockReset sx={{ mr: 1.5, fontSize: 20 }} />
                  Resetear contraseña
                </MenuItem>
              )}

              <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                <Logout sx={{ mr: 1.5, fontSize: 20 }} />
                Cerrar Sesión
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Sidebar - Usando versión dinámica basada en base de datos */}
      <SidebarDynamic open={sidebarOpen} toggleSidebar={toggleSidebar} />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          height: "100vh",
          overflow: "auto",
          pt: 8,
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Outlet />
        </Box>
      </Box>

      {/* Modal de Cambio de Contraseña */}
      <Dialog
        open={passwordDialogOpen}
        onClose={() => {}}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <LockReset sx={{ color: theme.palette.primary.main, fontSize: 32 }} />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Resetear Contraseña
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2.5 }}>
            {passwordError && (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {passwordError}
              </Alert>
            )}

            {passwordSuccess && (
              <Alert severity="success" sx={{ borderRadius: 2 }}>
                {passwordSuccess}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Contraseña Actual"
              type={showCurrentPassword ? "text" : "password"}
              value={passwordData.currentPassword}
              onChange={(e) => handlePasswordChange("currentPassword", e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      edge="end"
                    >
                      {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Nueva Contraseña"
              type={showNewPassword ? "text" : "password"}
              value={passwordData.newPassword}
              onChange={(e) => handlePasswordChange("newPassword", e.target.value)}
              disabled={loading}
              helperText="Mínimo 6 caracteres"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirmar Nueva Contraseña"
              type={showConfirmPassword ? "text" : "password"}
              value={passwordData.confirmPassword}
              onChange={(e) => handlePasswordChange("confirmPassword", e.target.value)}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleClosePasswordDialog}
            variant="outlined"
            color="inherit"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmitPasswordChange}
            variant="contained"
            disabled={loading}
            sx={{ minWidth: 100 }}
          >
            {loading ? "Guardando..." : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Toast */}
      <NotificationToast />

      {/* Reportar Modal */}
      <ReportarModal
        open={reportarModalOpen}
        onClose={() => setReportarModalOpen(false)}
        onSuccess={() => {
          // Disparar evento de refresh para actualizar la vista actual
          window.dispatchEvent(new CustomEvent('refreshData'));
        }}
      />
    </Box>
  );
};

export default AdminLayout;
